import { useState, useEffect, useCallback, useRef } from "react";
import { Match } from "../types";
import { updateMatchStatus } from "../../../lib/loggerApi";

/** FIBA standard: 10-minute quarters, 5-minute overtime */
export const QUARTER_MINUTES = 10;
export const OVERTIME_MINUTES = 5;
export const QUARTER_SECONDS = QUARTER_MINUTES * 60; // 600
export const OVERTIME_SECONDS = OVERTIME_MINUTES * 60; // 300

export type PeriodPhase =
  | "NOT_STARTED"
  | "Q1"
  | "BREAK_Q1"
  | "Q2"
  | "HALFTIME"
  | "Q3"
  | "BREAK_Q3"
  | "Q4"
  | "FULLTIME"
  | "OVERTIME"
  | "BREAK_OT"
  | "COMPLETED";

interface PeriodInfo {
  period: number;
  phase: PeriodPhase;
  isOvertime: boolean;
  extraTimeSeconds: number;
  shouldShowExtraTimeWarning: boolean;
  canTransitionToBreakQ1: boolean;
  canTransitionToQ2: boolean;
  canTransitionToHalftime: boolean;
  canTransitionToQ3: boolean;
  canTransitionToBreakQ3: boolean;
  canTransitionToQ4: boolean;
  canTransitionToFulltime: boolean;
  canTransitionToOvertime: boolean;
  canTransitionToBreakOT: boolean;
  canFinishMatch: boolean;
  /** Seconds elapsed since the current period started */
  periodElapsedSeconds: number;
  /** Minimum regulation seconds for the current period */
  periodMinimumSeconds: number;
}

// Map match status to phase
const statusToPhase = (status: string | undefined): PeriodPhase | null => {
  switch (status) {
    case "Scheduled":
    case "Pending":
      return "NOT_STARTED";
    case "Live":
    case "Live_Q1":
      return "Q1";
    case "Break_Q1":
      return "BREAK_Q1";
    case "Live_Q2":
      return "Q2";
    case "Halftime":
      return "HALFTIME";
    case "Live_Q3":
      return "Q3";
    case "Break_Q3":
      return "BREAK_Q3";
    case "Live_Q4":
      return "Q4";
    case "Fulltime":
      return "FULLTIME";
    case "Live_OT":
      return "OVERTIME";
    case "Break_OT":
      return "BREAK_OT";
    case "Completed":
      return "COMPLETED";
    default:
      return null;
  }
};

export const usePeriodManager = (
  match: Match | null,
  effectiveTime: number,
  globalTimeSeconds: number,
  clockMode: "EFFECTIVE" | "INEFFECTIVE",
  isClockRunning: boolean,
  handleModeSwitch: (mode: "EFFECTIVE" | "INEFFECTIVE") => void,
  fetchMatch: () => void,
  onTransitionError?: (info: {
    target: Match["status"];
    error: unknown;
  }) => void,
) => {
  const [currentPhase, setCurrentPhase] = useState<PeriodPhase>("NOT_STARTED");
  const [operatorPeriod, setOperatorPeriod] = useState(1);
  const [showExtraTimeAlert, setShowExtraTimeAlert] = useState(false);
  const lastMatchIdRef = useRef<string | null>(null);

  const phaseRank: Record<PeriodPhase, number> = {
    NOT_STARTED: 0,
    Q1: 1,
    BREAK_Q1: 2,
    Q2: 3,
    HALFTIME: 4,
    Q3: 5,
    BREAK_Q3: 6,
    Q4: 7,
    FULLTIME: 8,
    OVERTIME: 9,
    BREAK_OT: 10,
    COMPLETED: 11,
  };

  // Initialize phase from match status (monotonic: never regress the phase)
  useEffect(() => {
    if (!match?.status) return;

    const isResetMatch =
      match.status === "Pending" &&
      (match.match_time_seconds || 0) === 0 &&
      (match.ineffective_time_seconds || 0) === 0 &&
      !match.current_period_start_timestamp &&
      (!match.period_timestamps ||
        Object.keys(match.period_timestamps).length === 0);

    if (isResetMatch) {
      setCurrentPhase("NOT_STARTED");
      setOperatorPeriod(1);
      return;
    }

    const phaseFromStatus = statusToPhase(match.status);
    if (!phaseFromStatus) return;

    setCurrentPhase((prev) => {
      if (phaseRank[phaseFromStatus] < phaseRank[prev]) return prev;
      return phaseFromStatus;
    });
  }, [
    match?.status,
    match?.match_time_seconds,
    match?.ineffective_time_seconds,
    match?.current_period_start_timestamp,
    match?.period_timestamps,
  ]);

  useEffect(() => {
    if (!match?.id) return;
    if (lastMatchIdRef.current && lastMatchIdRef.current !== match.id) {
      const nextPhase = statusToPhase(match.status) ?? "NOT_STARTED";
      setCurrentPhase(nextPhase);
      setShowExtraTimeAlert(false);
      const periodMap: Record<string, number> = {
        Q1: 1, BREAK_Q1: 1, Q2: 2, HALFTIME: 2, Q3: 3, BREAK_Q3: 3,
        Q4: 4, FULLTIME: 4, OVERTIME: 5, BREAK_OT: 5,
      };
      setOperatorPeriod(periodMap[nextPhase] ?? 1);
    }
    lastMatchIdRef.current = match.id;
  }, [match?.id, match?.status]);

  // Update operator period based on phase
  useEffect(() => {
    const periodMap: Record<string, number> = {
      Q1: 1, BREAK_Q1: 1, Q2: 2, HALFTIME: 2, Q3: 3, BREAK_Q3: 3,
      Q4: 4, FULLTIME: 4, OVERTIME: 5, BREAK_OT: 5,
    };
    if (periodMap[currentPhase] !== undefined) {
      setOperatorPeriod(periodMap[currentPhase]);
    }
  }, [currentPhase]);

  // Calculate current period info
  const getPeriodInfo = useCallback((): PeriodInfo => {
    const phase: PeriodPhase = currentPhase;
    const period = operatorPeriod;
    const isOvertime = currentPhase === "OVERTIME" || currentPhase === "BREAK_OT";
    let extraTimeSeconds = 0;
    let shouldShowExtraTimeWarning = false;

    // Period key for timestamps
    const periodKeyMap: Record<string, string> = {
      Q1: "1", Q2: "2", Q3: "3", Q4: "4", OVERTIME: "5",
    };
    const periodKey = periodKeyMap[currentPhase] ?? null;

    const rawStartSeconds = periodKey
      ? match?.period_timestamps?.[periodKey]?.global_start_seconds
      : null;
    const startSeconds =
      typeof rawStartSeconds === "number" &&
      Number.isFinite(rawStartSeconds) &&
      rawStartSeconds >= 0 &&
      rawStartSeconds <= globalTimeSeconds
        ? rawStartSeconds
        : globalTimeSeconds;
    const elapsedSinceStart = Math.max(0, globalTimeSeconds - startSeconds);

    // Regulation seconds per period
    const periodMinimumSeconds = isOvertime ? OVERTIME_SECONDS : QUARTER_SECONDS;

    // Transition flags
    const canTransitionToBreakQ1 = currentPhase === "Q1";
    const canTransitionToQ2 = currentPhase === "BREAK_Q1";
    const canTransitionToHalftime = currentPhase === "Q2";
    const canTransitionToQ3 = currentPhase === "HALFTIME";
    const canTransitionToBreakQ3 = currentPhase === "Q3";
    const canTransitionToQ4 = currentPhase === "BREAK_Q3";
    const canTransitionToFulltime = currentPhase === "Q4";
    const canTransitionToOvertime = currentPhase === "FULLTIME" || currentPhase === "BREAK_OT";
    const canTransitionToBreakOT = currentPhase === "OVERTIME";
    const canFinishMatch = currentPhase === "FULLTIME" || currentPhase === "OVERTIME" || currentPhase === "BREAK_OT";

    // Extra time warning (quarter exceeded regulation)
    if (["Q1", "Q2", "Q3", "Q4"].includes(currentPhase)) {
      if (elapsedSinceStart >= QUARTER_SECONDS) {
        extraTimeSeconds = elapsedSinceStart - QUARTER_SECONDS;
        shouldShowExtraTimeWarning = true;
      }
    } else if (currentPhase === "OVERTIME") {
      if (elapsedSinceStart >= OVERTIME_SECONDS) {
        extraTimeSeconds = elapsedSinceStart - OVERTIME_SECONDS;
        shouldShowExtraTimeWarning = true;
      }
    }

    return {
      period,
      phase,
      isOvertime,
      extraTimeSeconds,
      shouldShowExtraTimeWarning,
      canTransitionToBreakQ1,
      canTransitionToQ2,
      canTransitionToHalftime,
      canTransitionToQ3,
      canTransitionToBreakQ3,
      canTransitionToQ4,
      canTransitionToFulltime,
      canTransitionToOvertime,
      canTransitionToBreakOT,
      canFinishMatch,
      periodElapsedSeconds: elapsedSinceStart,
      periodMinimumSeconds,
    };
  }, [effectiveTime, currentPhase, operatorPeriod, globalTimeSeconds, match]);

  const periodInfo = getPeriodInfo();

  // Show extra time alert
  useEffect(() => {
    if (
      periodInfo.shouldShowExtraTimeWarning &&
      isClockRunning &&
      clockMode === "EFFECTIVE"
    ) {
      setShowExtraTimeAlert(true);
    }
  }, [periodInfo.shouldShowExtraTimeWarning, isClockRunning, clockMode]);

  // Transition Helpers
  const performTransition = async (
    targetPhase: PeriodPhase,
    targetStatus: Match["status"],
    targetMode: "EFFECTIVE" | "INEFFECTIVE" | null,
  ) => {
    if (!match) return false;
    console.log(`🔄 Transitioning to ${targetPhase}...`);

    setCurrentPhase(targetPhase);
    setShowExtraTimeAlert(false);

    try {
      await updateMatchStatus(match.id, targetStatus);
      console.log(`✅ Match status updated to ${targetStatus}`);

      if (targetMode) {
        await handleModeSwitch(targetMode);
      }

      fetchMatch();
      return true;
    } catch (error) {
      console.error(`Failed to transition to ${targetStatus}:`, error);
      onTransitionError?.({ target: targetStatus as Match["status"], error });
      return false;
    }
  };

  // Basketball quarter transitions
  const transitionToBreakQ1 = () =>
    performTransition("BREAK_Q1", "Break_Q1", null);
  const transitionToQ2 = () =>
    performTransition("Q2", "Live_Q2", "EFFECTIVE");
  const transitionToHalftime = () =>
    performTransition("HALFTIME", "Halftime", null);
  const transitionToQ3 = () =>
    performTransition("Q3", "Live_Q3", "EFFECTIVE");
  const transitionToBreakQ3 = () =>
    performTransition("BREAK_Q3", "Break_Q3", null);
  const transitionToQ4 = () =>
    performTransition("Q4", "Live_Q4", "EFFECTIVE");
  const transitionToFulltime = () =>
    performTransition("FULLTIME", "Fulltime", null);
  const transitionToOvertime = () =>
    performTransition("OVERTIME", "Live_OT", "EFFECTIVE");
  const transitionToBreakOT = () =>
    performTransition("BREAK_OT", "Break_OT", null);
  const finishMatch = () =>
    performTransition("COMPLETED", "Completed", null);

  const dismissExtraTimeAlert = useCallback(() => {
    setShowExtraTimeAlert(false);
  }, []);

  return {
    operatorPeriod,
    currentPhase,
    periodInfo,
    showExtraTimeAlert,
    transitionToBreakQ1,
    transitionToQ2,
    transitionToHalftime,
    transitionToQ3,
    transitionToBreakQ3,
    transitionToQ4,
    transitionToFulltime,
    transitionToOvertime,
    transitionToBreakOT,
    finishMatch,
    dismissExtraTimeAlert,
  };
};
